<template>
    <div
        v-if="type === 'infinite'"
        ref="infiniteScroll"
        data-cy="infiniteScroll"
        class="d-flex align-items-center justify-center p-4"
    />

    <b-pagination
        v-else
        v-bind="$props"
        @input="$emit('input', $event)"
    />
</template>

<script>
    import {props} from 'bootstrap-vue/src/components/pagination/pagination';
    import debounce from 'lodash/debounce';

    /**
     * @group Tables
     * A component to utilize pagination, for lists or tables.
     * This enables either infinite scrolling support or traditional
     * pagination.
     * <br><br>
     * It takes in [all props that the `b-pagination` component does](
     * https://bootstrap-vue.org/docs/components/pagination#comp-ref-b-pagination-props),
     * as well as those described below.
     */
    export default {
        name: 'Pagination',
        props: {
            ...props,
            /**
             * Sets the default alignment of the traditional
             * pagination
             */
            align: {
                type: String,
                required: false,
                // `center`
                default: 'center',
            },
            /**
             * Changes how close the scrolling needs to be to the
             * bottom of the container for the infinite scrolling
             * event to be emitted
             */
            offset: {
                type: Number,
                required: false,
                // `200`
                default: 200,
            },
            /**
             * Sets the type of pagination
             */
            type: {
                // `infinite` / `traditional`
                type: String,
                required: false,
                // `infinite`
                default: 'infinite',
                validator: (value) => {
                    return ['infinite', 'traditional'].includes(value);
                },
            },
        },
        mounted() {
            if (this.type === 'infinite') {
                window.addEventListener('scroll', this.handleScroll);
                this.handleScroll();
            }
        },
        beforeDestroy() {
            this.type === 'infinite' && window.removeEventListener('scroll', this.handleScroll);
        },
        methods: {
            handleScroll: debounce(function() {
                const element = this.$refs.infiniteScroll;

                if (element?.getBoundingClientRect()?.bottom < (window.innerHeight + this.offset)) {
                    /**
                     * Emitted when the user scrolls to the bottom of the given container,
                     * with the given offset
                     */
                    this.$emit('scrolled');
                }
            }, 250),
        },
    };
</script>
